import React, { useState, useEffect, createContext, useContext, Suspense } from "react";
import "@/App.css";
import axios from "axios";
import { 
  Heart, Brain, Sun, Users, Clock, CheckCircle, Phone, Mail, Leaf,
  Waves, BatteryLow, Sprout,
  MapPin, Calendar, MessageCircle, ChevronDown, ChevronUp, 
  Sparkles, Shield, Target, Lightbulb, ArrowRight, Menu, X,
  BookOpen, FileText, Video, Headphones, Star, Quote, Globe, ArrowDown,
  Linkedin, Instagram
} from "lucide-react";
import { SOCIAL_LINKS, socialAria } from "@/social";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { Toaster, toast } from "sonner";
import AskAI from "./AskAI";
import { translations } from "@/translations";
import { ArticlesPage, ArticlePage, ArticleCard } from "@/ArticlesComponents";
import { articlesFor, findArticleByPath, articleRoute, translationFor } from "@/content/articles";
import { servicesData, ServiceCard } from "@/serviceCatalog";
import { useReveal } from "@/hooks/useReveal";
import { imgProps } from "@/media";

// Route-level code splitting: none of these pages are needed to paint the homepage.
const ServiceDetailPage = React.lazy(() => import("@/ServicesPages").then((m) => ({ default: m.ServiceDetailPage })));
const AboutFullPage = React.lazy(() => import("@/AboutPage").then((m) => ({ default: m.AboutFullPage })));
const ResourcesPage = React.lazy(() => import("@/ResourcesPage").then((m) => ({ default: m.ResourcesPage })));
const BlogLandingPage = React.lazy(() => import("@/BlogPage").then((m) => ({ default: m.BlogLandingPage })));
const LegalPage = React.lazy(() => import("@/LegalPages").then((m) => ({ default: m.LegalPage })));
const ServiceRetiredPage = React.lazy(() => import("@/ServiceRetiredPage").then((m) => ({ default: m.ServiceRetiredPage })));
const CookieConsent = React.lazy(() => import("@/CookieConsent").then((m) => ({ default: m.CookieConsent })));
const FloatingWhatsApp = React.lazy(() => import("@/FloatingWhatsApp").then((m) => ({ default: m.FloatingWhatsApp })));

// Reserves vertical space while a route chunk loads, so nothing shifts when it arrives.
const PageFallback = () => <div className="min-h-[70vh]" aria-hidden="true" />;

import ROUTES_CONFIG from "@/routes.json";
import RETIRED_CONFIG from "@/retiredRoutes.json";

const retiredRoute = (path) => RETIRED_CONFIG.routes.find((r) => r.path === path) || null;

const SITE = ROUTES_CONFIG.site;

const ROUTE_META = ROUTES_CONFIG.pages;

// Every localized page: URL pair per language. The URL is authoritative for language.
const PAGE_ROUTES = Object.fromEntries(
  ROUTES_CONFIG.pages.map((p) => [p.page, { ro: p.ro, en: p.en }])
);

// Retired URLs -> their single final destination (no chains). A true HTTP 301/308 must be
// configured on the host; see /app/memory/301_redirect_request.md.
const LEGACY_REDIRECTS = {
  '/resurse': '/blog/resurse',
  '/resources': '/en/blog/resources',
};

const normalizePath = (rawPath) => (rawPath.length > 1 ? rawPath.replace(/\/+$/, '') : rawPath) || '/';

const pageFromPath = (rawPath) => {
  const path = normalizePath(rawPath);
  if (findArticleByPath(path)) return 'article';
  if (retiredRoute(path)) return 'service-retired';
  for (const [page, paths] of Object.entries(PAGE_ROUTES)) {
    if (path === paths.ro || path === paths.en) return page;
  }
  return 'not-found';
};

// Language implied by the URL itself (null when the URL is not language-specific)
const langFromPath = (rawPath) => {
  const path = normalizePath(rawPath);
  const retired = retiredRoute(path);
  if (retired) return retired.lang;
  for (const paths of Object.values(PAGE_ROUTES)) {
    if (path === paths.en) return 'en';
    if (path === paths.ro) return 'ro';
  }
  const article = findArticleByPath(path);
  if (article) return article.language;
  return null;
};

// Real href for a page, in the requested language
export const routeFor = (page, lang = 'ro') => {
  const route = PAGE_ROUTES[page];
  return route ? route[lang] || route.ro : '/';
};

const setMeta = (selector, attr, value) => {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
};

// canonical / og:url exist on real pages only - an unknown URL is an error document
const setCanonical = (url) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!url) { el?.remove(); return; }
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', url);
};

const setOgUrl = (url) => {
  let el = document.querySelector('meta[property="og:url"]');
  if (!url) { el?.remove(); return; }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', 'og:url');
    document.head.appendChild(el);
  }
  el.setAttribute('content', url);
};

// Reciprocal language alternates for genuine translated pairs
const applyAlternates = (entry) => {
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
  if (!entry || !entry.ro || !entry.en) return;
  for (const [lang, p] of [['ro', entry.ro], ['en', entry.en]]) {
    const link = document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', lang);
    link.setAttribute('href', SITE + (p === '/' ? '/' : p));
    document.head.appendChild(link);
  }
};

// Keep canonical, title, description and lang in sync with the current URL
const applyRouteMeta = (language) => {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  // static article pages manage their own head (title, canonical, article metadata)
  if (findArticleByPath(path)) return;

  const url = SITE + (path === '/' ? '/' : path);
  const entry = ROUTE_META.find((p) => p.ro === path || p.en === path);
  applyAlternates(entry);
  // article-only metadata never leaks onto a regular page
  setMeta('meta[property="og:type"]', 'content', 'website');
  document.querySelectorAll('meta[property^="article:"]').forEach((el) => el.remove());
  document.getElementById('article-jsonld')?.remove();
  setMeta('meta[property="og:image"]', 'content', `${SITE}/og-default.jpg`);
  setMeta('meta[name="twitter:image"]', 'content', `${SITE}/og-default.jpg`);

  if (!entry) {
    // unknown URL: no canonical, no og:url, no hreflang (see NotFoundPage for noindex)
    setCanonical(null);
    setOgUrl(null);
    document.documentElement.setAttribute('lang', language === 'en' ? 'en' : 'ro');
    return;
  }

  setCanonical(url);
  setOgUrl(url);
  const isEn = entry.en === path;
  document.documentElement.setAttribute('lang', isEn ? 'en' : 'ro');
  const title = isEn ? entry.titleEn : entry.titleRo;
  const description = isEn ? entry.descEn : entry.descRo;
  document.title = title;
  setMeta('meta[name="description"]', 'content', description);
  setMeta('meta[property="og:title"]', 'content', title);
  setMeta('meta[property="og:description"]', 'content', description);
  setMeta('meta[name="twitter:title"]', 'content', title);
  setMeta('meta[name="twitter:description"]', 'content', description);
};

export const CALENDLY_URL = "https://calendly.com/ruxandrasterbet/therapy-session";
export const GOOGLE_MAPS_URL = "https://maps.app.goo.gl/fRnZJvocsXR2mtcF7";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Language Context
const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // the requested URL wins over any saved preference
    return langFromPath(window.location.pathname) || localStorage.getItem('lang') || 'ro';
  });
  
  const t = translations[language];
  
  const toggleLanguage = () => {
    const newLang = language === 'ro' ? 'en' : 'ro';
    setLanguage(newLang);
    localStorage.setItem('lang', newLang);
  };
  
  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Language Switcher Component
const LanguageSwitcher = ({ className = "", testId = "language-switcher" }) => {
  const { language, toggleLanguage } = useLanguage();
  
  return (
    <button
      data-testid={testId}
      onClick={toggleLanguage}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-sage/30 hover:border-sage hover:bg-sage/5 transition-all duration-300 text-[14px] font-medium text-sage-900 ${className}`}
      aria-label={language === 'ro' ? 'EN — switch to English' : 'RO — comută la română'}
    >
      <Globe size={16} strokeWidth={1.5} />
      <span>{language === 'ro' ? 'EN' : 'RO'}</span>
    </button>
  );
};

// Sticky Banner Component
const StickyBanner = () => {
  const [isVisible, setIsVisible] = useState(true);
  const { t } = useLanguage();
  
  if (!isVisible) return null;
  
  return (
    <div 
      data-testid="sticky-banner"
      className="fixed top-0 w-full z-50 bg-sage text-white py-2 md:py-3 px-4 text-center text-xs md:text-sm font-medium shadow-md"
    >
      <div className="container-page flex items-center justify-center gap-2 md:gap-4 flex-wrap pr-8">
        <span className="hidden sm:inline">
          {t.bannerText} <strong>{t.bannerDiscount}</strong> 🧡
        </span>
        <span className="sm:hidden text-xs">
          {t.bannerText.split(' ').slice(0, 3).join(' ')}... <strong>{t.bannerDiscount}</strong> 🧡
        </span>
        <button
          data-testid="banner-cta-btn"
          onClick={() => document.getElementById('programare').scrollIntoView({ behavior: 'smooth' })}
          className="bg-warm-orange hover:bg-warm-orangeDark text-white px-3 md:px-4 py-1 md:py-1.5 rounded-full text-xs md:text-sm font-medium transition-all duration-300"
        >
          {t.bannerCta}
        </button>
        <button 
          onClick={() => setIsVisible(false)}
          className="absolute right-2 md:right-4 text-white/80 hover:text-white p-1"
          aria-label="Close banner"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

// Navigation Component
const Navigation = ({ bannerVisible, navigateTo }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpenMenu, setMobileOpenMenu] = useState(null);
  const { t, language } = useLanguage();
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const navLinks = [
    { label: t.navAbout, href: '#despre', id: 'nav-about' },
    {
      label: t.navServices,
      id: 'nav-services',
      children: [
        { label: t.svc1Title, page: 'service-psihoterapie-adulti', id: 'nav-services-adults' },
        { label: t.svc2Title, page: 'service-consiliere-copii', id: 'nav-services-children' },
        { label: t.svc3Title, page: 'service-consiliere-vocationala', id: 'nav-services-vocational' },
      ],
    },
    {
      label: t.navBlog,
      id: 'nav-blog',
      page: 'blog',
      children: [
        { label: t.navBlogArticles, page: 'blog-articles', id: 'nav-blog-articles' },
        { label: t.navBlogResources, page: 'resources', id: 'nav-blog-resources' },
      ],
    },
    { label: t.navContact, href: '#programare', id: 'nav-contact' },
  ];

  const handleNavClick = (e, link) => {
    // If on a non-home page, go home first then scroll
    const isHome = window.location.pathname === '/' || window.location.pathname === '';
    if (!isHome) {
      e.preventDefault();
      if (navigateTo) {
        navigateTo('home');
      } else {
        window.location.href = '/' + link.href;
        return;
      }
      setTimeout(() => {
        const sectionId = link.href.replace('#', '');
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed w-full z-40 transition-all duration-300 ${
        bannerVisible ? 'top-[48px] md:top-12' : 'top-0'
      } ${isScrolled ? 'bg-ivory/95 backdrop-blur-md border-b border-sage/10' : 'bg-ivory/80 backdrop-blur-sm border-b border-sage/5'}`}
    >
      <div className="container-page">
        <div className="flex items-center justify-between h-16 md:h-[72px]">
          <a 
            href={routeFor('home', language)}
            onClick={(e) => { e.preventDefault(); if (navigateTo) navigateTo('home'); else window.location.href = routeFor('home', language); }}
            className="flex flex-col leading-tight"
          >
            <span className="font-serif text-xl md:text-[1.6rem] text-sage-900 font-semibold tracking-tight">Ruxandra Sterbet</span>
            <span data-testid="nav-tagline" className="text-[10px] md:text-[11.5px] uppercase tracking-[0.16em] text-sage-900/70 mt-1">
              {t.navTagline}
            </span>
          </a>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-7">
            {navLinks.map((link) => (
              link.children ? (
                <div
                  key={link.id}
                  className="relative"
                  onMouseEnter={() => setOpenMenu(link.id)}
                  onMouseLeave={() => setOpenMenu(null)}
                >
                  {link.page ? (
                    <a
                      data-testid={link.id}
                      href={routeFor(link.page, language)}
                      onClick={(e) => { e.preventDefault(); setOpenMenu(null); navigateTo(link.page); }}
                      className="flex items-center gap-1 text-sage-900/80 hover:text-sage transition-colors text-[15px] font-medium"
                    >
                      {link.label}
                      <ChevronDown
                        size={15}
                        className={`transition-transform duration-300 ${openMenu === link.id ? 'rotate-180' : ''}`}
                      />
                    </a>
                  ) : (
                    <button
                      data-testid={link.id}
                      onClick={() => setOpenMenu(openMenu === link.id ? null : link.id)}
                      className="flex items-center gap-1 text-sage-900/80 hover:text-sage transition-colors text-[15px] font-medium"
                    >
                      {link.label}
                      <ChevronDown
                        size={15}
                        className={`transition-transform duration-300 ${openMenu === link.id ? 'rotate-180' : ''}`}
                      />
                    </button>
                  )}
                  <div
                    data-testid={`${link.id}-dropdown`}
                    className={`absolute left-0 top-full pt-3 w-[24rem] z-50 transition-all duration-300 origin-top ${
                      openMenu === link.id
                        ? 'opacity-100 translate-y-0 pointer-events-auto'
                        : 'opacity-0 -translate-y-2 pointer-events-none'
                    }`}
                  >
                    <div className="bg-ivory rounded-2xl border border-sage/25 shadow-[0_24px_50px_-24px_rgba(45,58,49,0.35)] py-3 overflow-hidden">
                      {link.children.map((child) => (
                        <a
                          key={child.id}
                          data-testid={child.id}
                          href={routeFor(child.page, language)}
                          onClick={(e) => { e.preventDefault(); setOpenMenu(null); navigateTo(child.page); }}
                          className="block w-full text-left px-6 py-3.5 text-[15px] leading-snug text-sage-900 hover:text-sage hover:bg-sage/10 transition-colors duration-200"
                        >
                          {child.label}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <a 
                  key={link.id}
                  data-testid={link.id}
                  href={link.page ? routeFor(link.page, language) : (link.href || '#')}
                  onClick={(e) => {
                    if (link.page) { e.preventDefault(); navigateTo(link.page); return; }
                    handleNavClick(e, link);
                  }}
                  className="text-sage-900/80 hover:text-sage transition-colors text-[15px] font-medium"
                >
                  {link.label}
                </a>
              )
            ))}
            <LanguageSwitcher />
            <a
              data-testid="nav-cta-btn"
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-clay hover:bg-clay-dark text-white px-6 py-2.5 rounded-full text-[14.5px] font-medium tracking-wide transition-all duration-300 shadow-[0_8px_24px_-12px_rgba(201,138,107,0.8)] hover:shadow-[0_10px_28px_-10px_rgba(201,138,107,0.9)]"
            >
              {t.navCta}
            </a>
          </div>
          
          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSwitcher className="text-xs px-2 py-1" testId="mobile-language-switcher" />
            <button 
              data-testid="mobile-menu-btn"
              className="text-sage-900"
              aria-label={mobileMenuOpen
                ? (language === 'ro' ? 'Închide meniul de navigare' : 'Close navigation menu')
                : (language === 'ro' ? 'Deschide meniul de navigare' : 'Open navigation menu')}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div id="mobile-menu" className="md:hidden bg-cream/95 backdrop-blur-md border-t border-stone-100 py-4">
            {navLinks.map((link) => (
              link.children ? (
                <div key={link.id}>
                  {link.page ? (
                    <div className="w-full flex items-center justify-between text-sage-900/80">
                      <a
                        data-testid={`mobile-${link.id}`}
                        href={routeFor(link.page, language)}
                        onClick={(e) => {
                          e.preventDefault();
                          setMobileMenuOpen(false);
                          setMobileOpenMenu(null);
                          navigateTo(link.page);
                        }}
                        className="flex-grow py-3 px-4 text-left hover:text-sage transition-colors"
                      >
                        {link.label}
                      </a>
                      <button
                        data-testid={`mobile-${link.id}-toggle`}
                        aria-label={link.label}
                        aria-expanded={mobileOpenMenu === link.id}
                        onClick={() => setMobileOpenMenu(mobileOpenMenu === link.id ? null : link.id)}
                        className="py-3 px-4 hover:text-sage transition-colors"
                      >
                        <ChevronDown
                          size={16}
                          className={`transition-transform duration-300 ${mobileOpenMenu === link.id ? 'rotate-180' : ''}`}
                        />
                      </button>
                    </div>
                  ) : (
                    <button
                      data-testid={`mobile-${link.id}`}
                      onClick={() => setMobileOpenMenu(mobileOpenMenu === link.id ? null : link.id)}
                      className="w-full flex items-center justify-between py-3 px-4 text-sage-900/80 hover:text-sage hover:bg-sage/5 transition-colors"
                    >
                      {link.label}
                      <ChevronDown
                        size={16}
                        className={`transition-transform duration-300 ${mobileOpenMenu === link.id ? 'rotate-180' : ''}`}
                      />
                    </button>
                  )}
                  {mobileOpenMenu === link.id && (
                    <div data-testid={`mobile-${link.id}-dropdown`} className="bg-sage/5">
                      {link.children.map((child) => (
                        <a
                          key={child.id}
                          data-testid={`mobile-${child.id}`}
                          href={routeFor(child.page, language)}
                          onClick={(e) => {
                            e.preventDefault();
                            setMobileMenuOpen(false);
                            setMobileOpenMenu(null);
                            navigateTo(child.page);
                          }}
                          className="block w-full text-left py-3 pl-8 pr-4 text-[14px] leading-snug text-sage-900/70 hover:text-sage transition-colors"
                        >
                          {child.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <a 
                  key={link.id}
                  data-testid={`mobile-${link.id}`}
                  href={link.page ? routeFor(link.page, language) : (link.href || '#')}
                  onClick={(e) => {
                    setMobileMenuOpen(false);
                    if (link.page) { e.preventDefault(); navigateTo(link.page); return; }
                    handleNavClick(e, link);
                  }}
                  className="block py-3 px-4 text-sage-900/80 hover:text-sage hover:bg-sage/5 transition-colors"
                >
                  {link.label}
                </a>
              )
            ))}
            <div className="px-4 pt-4">
              <a
                data-testid="mobile-nav-cta-btn"
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-center w-full bg-clay hover:bg-clay-dark text-white px-6 py-3 rounded-full text-[15px] font-medium transition-all duration-300"
              >
                {t.navCta}
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero headline: only the final key word rotates (CSS-driven), in a box sized to the longest word
const HeroRotatingWord = ({ words }) => (
  <span className="hero-rotator text-sage">
    {words.map((word) => (
      <span key={word} className="hero-word">{word}</span>
    ))}
  </span>
);

// Hero Section
const HeroSection = () => {
  const { t } = useLanguage();

  const points = [
    { icon: Shield, text: t.heroBullet1 },
    { icon: Leaf, text: t.heroBullet2 },
    { icon: Target, text: t.heroBullet3 },
  ];

  return (
    <section
      data-testid="hero-section"
      className="relative bg-ivory overflow-hidden pt-24 md:pt-28 pb-16 md:pb-20"
    >
      {/* Desktop portrait — bleeds to the right edge */}
      <div className="hidden lg:block absolute top-[72px] right-0 bottom-0 w-[47%]">
        <img
          {...imgProps('portrait', '(min-width: 1024px) 47vw, 100vw')}
          alt="Ruxandra Sterbet"
          data-testid="hero-portrait"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="motion-enter-fade w-full h-full object-cover object-[center_30%]"
          style={{ animationDelay: '180ms' }}
        />
        <div className="absolute inset-y-0 left-0 w-36 bg-gradient-to-r from-ivory via-ivory/70 to-transparent"></div>
        <div className="absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-ivory/60 to-transparent"></div>
      </div>

      <div className="relative w-full container-page">
        <div className="grid lg:grid-cols-[54%_46%]">
          <div className="lg:pr-10 lg:py-8">
            <p className="motion-enter text-[12px] md:text-[13px] uppercase tracking-[0.26em] text-sage-900/70 mb-7" style={{ animationDelay: '40ms' }}>
              {t.heroEyebrow}
            </p>

            <h1
              className="motion-enter font-serif text-[2.2rem] sm:text-[2.6rem] lg:text-[2.65rem] xl:text-[2.9rem] leading-[1.16] tracking-[-0.01em] mb-7"
              style={{ animationDelay: '150ms' }}
            >
              <span className="sr-only">{t.heroTitleFull}</span>
              <span aria-hidden="true">
                <span className="text-sage-900">{t.heroTitleL1}</span>
                <br />
                <span className="text-sage-900">{t.heroTitleL2} </span>
                <span className="text-sage">{t.heroTitleL3}</span>
                <br />
                <HeroRotatingWord words={t.heroTitleWords} />
              </span>
            </h1>

            <p
              className="motion-enter text-[17px] md:text-[18.5px] text-sage-900/80 leading-[1.8] max-w-[38rem] mb-9 font-light"
              style={{ animationDelay: '270ms' }}
            >
              {t.heroSubtitleA}
              <span className="font-medium text-sage-900/85">{t.heroSubtitleB}</span>
              {t.heroSubtitleC}
              <span className="font-medium text-sage-900/85">{t.heroSubtitleD}</span>
              {t.heroSubtitleE}
              <span className="font-medium text-sage-900/85">{t.heroSubtitleF}</span>
            </p>

            <div className="space-y-4 mb-10">
              {points.map((item, i) => (
                <div
                  key={i}
                  data-testid={`hero-point-${i}`}
                  className="motion-enter flex items-start gap-3.5 text-[16.5px] text-sage-900/80"
                  style={{ animationDelay: `${380 + i * 110}ms` }}
                >
                  <item.icon className="text-sage mt-1 flex-shrink-0" size={20} strokeWidth={1.25} />
                  <span className="leading-relaxed">{item.text}</span>
                </div>
              ))}
            </div>

            <div
              className="motion-enter flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5"
              style={{ animationDelay: '720ms' }}
            >
              <a
                data-testid="hero-cta-btn"
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-2.5 bg-clay hover:bg-clay-dark text-white px-8 py-4 rounded-full text-[15.5px] font-medium tracking-wide transition-all duration-200 shadow-[0_14px_34px_-18px_rgba(201,138,107,0.95)] md:hover:-translate-y-0.5 md:hover:shadow-[0_18px_40px_-18px_rgba(201,138,107,0.95)]"
              >
                {t.heroCta}
                <ArrowRight size={17} strokeWidth={1.75} className="transition-transform duration-200 group-hover:translate-x-1" />
              </a>
              <button
                data-testid="hero-secondary-btn"
                onClick={() => document.getElementById('servicii')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center justify-center border border-sage/40 text-sage-900/80 hover:border-sage hover:text-sage bg-transparent px-8 py-4 rounded-full text-[15.5px] font-medium tracking-wide transition-all duration-300"
              >
                {t.heroSecondary}
              </button>
            </div>
          </div>

          {/* Mobile / tablet portrait */}
          <div className="lg:hidden mt-12 bleed-x md:bleed-none overflow-hidden md:rounded-[1.75rem]">
            <img
              {...imgProps('portrait', '100vw')}
              alt="Ruxandra Sterbet"
              data-testid="hero-portrait-mobile"
              loading="lazy"
              decoding="async"
              className="w-full h-auto object-cover"
            />
          </div>
        </div>
      </div>

      {/* Scroll to explore */}
      <div
        data-testid="hero-scroll-indicator"
        className="hidden lg:flex absolute bottom-5 left-[48%] -translate-x-1/2 flex-col items-center"
      >
        <span className="text-[10px] uppercase tracking-[0.28em] text-sage-900/70">
          {t.heroScroll}
        </span>
        <ArrowDown size={18} strokeWidth={1} className="text-sage-900/70 mt-1.5 animate-bounce-slow" />
      </div>

    </section>
  );
};

// Validation Section
const ValidationSection = () => {
  const { t } = useLanguage();
  const [sectionRef, isVisible] = useReveal(0.15);

  const items = [
    { icon: Brain, label: t.validationSymptom1, color: 'text-sage', bg: 'bg-sage/12' },
    { icon: Waves, label: t.validationSymptom2, color: 'text-[#8FA3B2]', bg: 'bg-[#A4B4C1]/15' },
    { icon: Users, label: t.validationSymptom3, color: 'text-clay', bg: 'bg-clay/12' },
    { icon: BatteryLow, label: t.validationSymptom4, color: 'text-[#B9A06B]', bg: 'bg-[#C9B27E]/15' },
    { icon: Sprout, label: t.validationSymptom5, color: 'text-sage-dark', bg: 'bg-sage/12' },
  ];

  return (
    <section
      id="validation"
      ref={sectionRef}
      data-testid="validation-section"
      className="bg-cream pt-10 md:pt-14 pb-12 md:pb-16"
    >
      <div className="container-content">
        <div className="text-center max-w-4xl mx-auto mb-7 md:mb-9">
          <h2 className={`font-serif text-[1.8rem] md:text-[2.25rem] leading-[1.25] text-sage-900 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}>
            {t.validationTitle1} {t.validationTitle2}
          </h2>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
          {items.map((item, i) => (
            <div
              key={i}
              data-testid={`validation-item-${i}`}
              className={`group bg-white border border-sage/12 rounded-2xl px-5 py-8 flex flex-col items-center text-center shadow-[0_16px_36px_-32px_rgba(45,58,49,0.6)] md:hover:-translate-y-[3px] md:hover:shadow-[0_24px_46px_-28px_rgba(45,58,49,0.3)] md:hover:border-sage/30 transition-all duration-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
              style={{ transitionDelay: `${120 + i * 120}ms` }}
            >
              <span className={`w-14 h-14 rounded-full ${item.bg} flex items-center justify-center mb-4 transition-transform duration-500 md:group-hover:scale-105`}>
                <item.icon className={item.color} size={26} strokeWidth={1.4} />
              </span>
              <p className="text-[17.5px] leading-snug text-sage-900/85 group-hover:text-sage-900 transition-colors duration-300">{item.label}</p>
            </div>
          ))}
        </div>

        <div
          data-testid="validation-closing"
          className={`flex items-center justify-center gap-4 md:gap-6 mt-8 md:mt-10 transition-all duration-700 ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ transitionDelay: '780ms' }}
        >
          <span
            className={`hidden sm:block h-px w-16 md:w-24 bg-sage/25 origin-right transition-transform duration-700 ${isVisible ? 'scale-x-100' : 'scale-x-0'}`}
            style={{ transitionDelay: '900ms' }}
          ></span>
          <p className="font-serif text-[18px] md:text-[20px] text-sage-900/70 text-center">
            {t.validationFooterNote}
          </p>
          <span
            className={`hidden sm:block h-px w-16 md:w-24 bg-sage/25 origin-left transition-transform duration-700 ${isVisible ? 'scale-x-100' : 'scale-x-0'}`}
            style={{ transitionDelay: '900ms' }}
          ></span>
        </div>
      </div>
    </section>
  );
};

// A Different Perspective band
const PerspectiveSection = () => {
  const { t } = useLanguage();
  const [sectionRef, isVisible] = useReveal(0.15);

  return (
    <section
      ref={sectionRef}
      data-testid="perspective-section"
      className="bg-ivory"
    >
      <div className="container-page-flush grid lg:grid-cols-[18%_1fr]">
        <div className="h-44 sm:h-56 lg:h-auto lg:max-h-[19rem] overflow-hidden">
          <img
            {...imgProps('perspective', '(max-width: 1024px) 100vw, 18vw')}
            alt=""
            data-testid="perspective-image"
            loading="lazy"
            decoding="async"
            className={`w-full h-full object-cover object-[center_60%] transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
          />
        </div>

        <div className="px-6 md:px-12 lg:px-12 py-9 md:py-10">
          <div className="max-w-none">
            <p className={`text-[11px] md:text-[12px] uppercase tracking-[0.28em] text-sage-900/70 mb-4 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}>
              {t.perspectiveLabel}
            </p>

            <h2 className={`font-serif text-[1.6rem] md:text-[2.1rem] lg:text-[2.25rem] leading-[1.2] text-sage-900 mb-5 whitespace-normal lg:whitespace-nowrap transition-all duration-700 delay-100 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}>
              {t.perspectiveTitle1} <span className="italic text-sage">{t.perspectiveTitleItalic}</span>{' '}
              {t.perspectiveTitle2}
            </h2>

            <div className={`space-y-3 transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}>
              <p data-testid="perspective-text-1" className="text-[15px] md:text-[15.5px] text-sage-900/70 leading-[1.8] font-light">
                {t.perspectiveP1}
              </p>
              <p data-testid="perspective-text-2" className="text-[15px] md:text-[15.5px] text-sage-900/70 leading-[1.8] font-light">
                {t.perspectiveP2a}
                <strong className="font-bold text-sage-900/80">{t.perspectiveP2b}</strong>
                {t.perspectiveP2c}
                <strong
                  className={`font-bold transition-colors duration-700 ${isVisible ? 'text-sage' : 'text-sage-900/70'}`}
                  style={{ transitionDelay: '600ms' }}
                >
                  {t.perspectiveP2d}
                </strong>
                {t.perspectiveP2e}
                <span className="font-semibold">{t.perspectiveP2f}</span>
                {t.perspectiveP2g}
                <span className="font-semibold text-sage-900/85">{t.perspectiveP2h}</span>
                .
              </p>
              <p data-testid="perspective-text-3" className="font-serif italic text-[16.5px] md:text-[17.5px] text-sage/90 leading-[1.75]">
                {t.perspectiveP3}
              </p>
            </div>

          </div>

        </div>
      </div>
    </section>
  );
};


// Services Section
const ServicesSection = ({ navigateTo }) => {
  const { t, language } = useLanguage();
  const [sectionRef, isVisible] = useReveal(0.15);

  const handleBook = () => {
    document.getElementById('programare')?.scrollIntoView({ behavior: 'smooth' });
  };
  
  return (
    <section 
      id="servicii"
      ref={sectionRef}
      data-testid="services-section"
      className="py-10 md:py-16 md:pb-8 bg-cream overflow-hidden"
    >
      <div className="container-content">
        <div className={`text-center mb-12 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <p data-testid="services-tagline" className="font-serif italic text-base md:text-lg text-sage/90 mb-3">
            {t.servicesTagline}
          </p>
          <h2 data-testid="services-title" className="font-serif text-3xl md:text-[2.5rem] text-sage-900">
            {t.servicesTitle}
          </h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {servicesData.map((service, i) => (
            <ServiceCard 
              key={service.id}
              service={service}
              index={i}
              isVisible={isVisible}
              t={t}
              learnMoreHref={routeFor(service.id, language)}
              onLearnMore={(id) => navigateTo(id)}
              onBook={handleBook}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const { t } = useLanguage();
  const [sectionRef, isVisible] = useReveal(0.2);

  const steps = [
    { number: '01', title: t.howStep1Title, description: t.howStep1Desc },
    { number: '02', title: t.howStep2Title, description: t.howStep2Desc },
    { number: '03', title: t.howStep3Title, description: t.howStep3Desc },
    { number: '04', title: t.howStep4Title, description: t.howStep4Desc },
  ];
  
  return (
    <section 
      ref={sectionRef}
      id="cum-functioneaza"
      data-testid="how-it-works-section"
      className="pt-4 pb-7 md:pt-5 md:pb-11 bg-cream overflow-hidden"
    >
      <div className="container-content">
        <div className={`text-center mb-8 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <p className="text-sm uppercase tracking-widest text-sage mb-3 font-medium">{t.howLabel}</p>
          <h2 className="font-serif text-3xl md:text-4xl text-sage-900 mb-0">
            {t.howTitle}
          </h2>
        </div>
        
        <div className="grid md:grid-cols-4 gap-8">
          {steps.map((step, i) => (
            <div 
              key={i} 
              data-testid={`step-${i}`} 
              className={`relative group transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
              style={{ transitionDelay: `${150 + i * 150}ms` }}
            >
              <span
                className={`font-serif text-[2.6rem] leading-none inline-block mb-3 transition-colors duration-700 md:group-hover:text-[#A4552F] ${
                  isVisible ? 'text-[#D98F6F]' : 'text-[#D98F6F]/25'
                }`}
                style={{ transitionDelay: `${300 + i * 150}ms` }}
              >{step.number}</span>
              <h3
                className={`font-serif text-xl mb-3 transition-colors duration-700 md:group-hover:text-sage ${
                  isVisible ? 'text-sage-900' : 'text-sage-900/45'
                }`}
                style={{ transitionDelay: `${360 + i * 150}ms` }}
              >{step.title}</h3>
              <p
                className={`text-sage-900/70 transition-opacity duration-700 ${isVisible ? 'opacity-100' : 'opacity-50'}`}
                style={{ transitionDelay: `${420 + i * 150}ms` }}
              >{step.description}</p>
              {i < steps.length - 1 && (
                <div className={`hidden md:block absolute top-8 right-0 w-full h-px bg-sage/20 translate-x-1/2 transition-transform duration-700 ${
                  isVisible ? 'scale-x-100' : 'scale-x-0'
                }`} style={{ transitionDelay: `${480 + i * 150}ms`, transformOrigin: 'left' }}></div>
              )}
            </div>
          ))}
        </div>
        
        <p className={`text-center mt-8 text-lg text-sage-900/70 italic transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
        }`} style={{ transitionDelay: '1200ms' }}>
          {t.howNote}
        </p>
      </div>
    </section>
  );
};

// About Section
const aboutPhotos = [
  "about-1",
  "about-2",
];

const AboutSection = ({ navigateTo }) => {
  const { t, language } = useLanguage();
  const [photoIndex, setPhotoIndex] = useState(0);
  const [sectionRef, isVisible] = useReveal(0.2);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const interval = setInterval(() => {
      setPhotoIndex((prev) => (prev + 1) % aboutPhotos.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section 
      id="despre"
      ref={sectionRef}
      data-testid="about-section"
      className="py-10 md:py-16 bg-cream-dark overflow-hidden"
    >
      <div className="container-page">
        <div className="grid lg:grid-cols-[38%_1fr] gap-10 lg:gap-14 items-start">
          <div className={`relative transition-opacity duration-1000 ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[3/4]" data-testid="about-carousel">
              {aboutPhotos.map((src, i) => (
                <img 
                  key={src}
                  {...imgProps(src, '(max-width: 1024px) 100vw, 460px')}
                  alt="Ruxandra Sterbet - Psihoterapeut"
                  data-testid={`about-photo-${i}`}
                  loading="lazy"
                  decoding="async"
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${
                    photoIndex === i ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              ))}
            </div>
          </div>
          
          <div className={`transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
          }`} style={{ transitionDelay: '150ms' }}>
            <p className="text-[11px] md:text-[12px] uppercase tracking-[0.28em] text-sage-900/70 mb-3">{t.aboutLabel}</p>
            <h2 className="font-serif text-[2.2rem] md:text-[3rem] leading-[1.05] text-sage-900 mb-5">
              {t.aboutName}
            </h2>
            <div className="h-px bg-sage-900/15 mb-6"></div>

            <div data-testid="about-credentials" className={`mb-6 space-y-1 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
            }`} style={{ transitionDelay: '400ms' }}>
              <p className="text-[16px] md:text-[16.5px] text-sage-900/70 leading-[1.7]">{t.aboutCredentials1}</p>
              <p className="text-[16px] md:text-[16.5px] text-sage-900/70 leading-[1.7]">{t.aboutCredentials2}</p>
            </div>

            <div className={`space-y-4 mb-9 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
            }`} style={{ transitionDelay: '560ms' }}>
              <p className="text-[15.5px] md:text-[16px] text-sage-900/75 leading-[1.75]">{t.aboutPara1}</p>
              <p className="text-[15.5px] md:text-[16px] text-sage-900/75 leading-[1.75]">{t.aboutPara2}</p>
              <p className="text-[15.5px] md:text-[16px] text-sage-900/75 leading-[1.75]">{t.aboutPara3}</p>
              {t.homeAboutLangNote && (
                <p data-testid="home-about-lang-note" className="text-[15.5px] md:text-[16px] text-sage-900/75 leading-[1.75]">{t.homeAboutLangNote}</p>
              )}
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-8 mb-10">
              {[
                { icon: Users, title: t.aboutHl1Title, desc: t.aboutHl1Desc },
                { icon: BookOpen, title: t.aboutHl2Title, desc: t.aboutHl2Desc },
                { icon: FileText, title: t.aboutHl3Title, desc: t.aboutHl3Desc },
                { icon: Heart, title: t.aboutHl4Title, desc: t.aboutHl4Desc },
              ].map((item, i) => (
                <div
                  key={i}
                  data-testid={`about-highlight-${i}`}
                  className={`px-0 lg:px-6 transition-all duration-700 ${i > 0 ? 'lg:border-l lg:border-sage-900/12' : ''} ${i % 2 === 1 ? 'pl-6 border-l border-sage-900/12 lg:pl-6' : ''} ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
                  }`}
                  style={{ transitionDelay: `${720 + i * 130}ms` }}
                >
                  <span className="w-12 h-12 rounded-full bg-sage/12 flex items-center justify-center mb-4">
                    <item.icon size={21} strokeWidth={1.5} className="text-sage" />
                  </span>
                  <h3 className="font-serif text-[1.05rem] md:text-[1.15rem] text-sage-900 mb-1.5">{item.title}</h3>
                  <p className="text-[14.5px] text-sage-900/70 leading-[1.6] max-w-[15rem]">{item.desc}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-5">
              <a
                data-testid="about-read-more-btn"
                href={routeFor('about-full', language)}
                onClick={(e) => { e.preventDefault(); navigateTo('about-full'); }}
                className="group inline-flex items-center justify-center gap-2.5 bg-[#AA6244] hover:bg-[#8F5236] text-white px-8 py-4 rounded-full text-[15.5px] font-medium transition-all duration-300 shadow-md hover:shadow-lg"
              >
                {t.aboutReadMore}
                <ArrowRight size={17} strokeWidth={1.75} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  const { t } = useLanguage();
  const [sectionRef, isVisible] = useReveal(0.1);
  
  const packages = [
    { sessions: t.pricingPkg1, price: '630 lei', perSession: t.pricingPerSession },
    { sessions: t.pricingPkg2, price: '665 lei', perSession: t.pricingPerSession },
    { sessions: t.pricingPkg3, price: '700 lei', perSession: t.pricingPerSession },
  ];
  
  return (
    <section 
      id="preturi"
      ref={sectionRef}
      data-testid="pricing-section"
      className="py-10 md:py-16 bg-cream overflow-hidden"
    >
      <div className="container-content">
        <div className={`text-center mb-10 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <p className="text-sm uppercase tracking-widest text-sage mb-4 font-medium">{t.pricingLabel}</p>
          <h2 className="font-serif text-3xl md:text-4xl text-sage-900 mb-4">
            {t.pricingTitle}
          </h2>
          <p className="text-sage-900/70">{t.pricingSession}</p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className={`bg-white p-8 rounded-2xl border border-sage shadow-lg transition-all duration-500 hover:shadow-xl hover:-translate-y-2 group ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`} style={{ transitionDelay: '200ms' }}>
            <h3 className="font-serif text-2xl text-sage-900 mb-2 group-hover:text-sage transition-colors duration-300">{t.service1Title}</h3>
            <p className="text-3xl font-medium text-warm-orange mb-4">{t.service1Price}</p>
            <p className="text-sage-900/70 mb-6">{t.pricingDesc1}</p>
            <a
              data-testid="pricing-cta-1"
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center w-full bg-warm-orange hover:bg-warm-orangeDark text-white py-3 rounded-full font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg"
            >
              {t.pricingCta}
            </a>
          </div>
          
          <div className={`bg-white p-8 rounded-2xl border border-stone-100 hover:border-sage/30 transition-all duration-500 hover:shadow-xl hover:-translate-y-2 group ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`} style={{ transitionDelay: '350ms' }}>
            <h3 className="font-serif text-2xl text-sage-900 mb-2 group-hover:text-sage transition-colors duration-300">{t.service2Title}</h3>
            <p className="text-3xl font-medium text-warm-orange mb-4">{t.service2Price}</p>
            <p className="text-sage-900/70 mb-6">{t.pricingDesc2}</p>
            <a
              data-testid="pricing-cta-2"
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center w-full bg-sage hover:bg-sage-dark text-white py-3 rounded-full font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg"
            >
              {t.pricingCta}
            </a>
          </div>
          
          <div className={`bg-white p-8 rounded-2xl border border-stone-100 hover:border-sage/30 transition-all duration-500 hover:shadow-xl hover:-translate-y-2 group ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`} style={{ transitionDelay: '500ms' }}>
            <h3 className="font-serif text-2xl text-sage-900 mb-2 group-hover:text-sage transition-colors duration-300">{t.service3Title}</h3>
            <p className="text-3xl font-medium text-warm-orange mb-4">{t.service3Price}</p>
            <p className="text-sage-900/70 mb-6">{t.pricingDesc3}</p>
            <a
              data-testid="pricing-cta-3"
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center w-full bg-sage hover:bg-sage-dark text-white py-3 rounded-full font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg"
            >
              {t.pricingCta}
            </a>
          </div>
        </div>
        
        {/* Packages */}
        <div className={`bg-sage/5 p-8 rounded-2xl transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`} style={{ transitionDelay: '700ms' }}>
          <h3 className="font-serif text-xl text-sage-900 mb-6 text-center">{t.pricingPackages}</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {packages.map((pkg, i) => (
              <div 
                key={i} 
                className={`bg-white p-6 rounded-xl text-center transition-all duration-500 hover:shadow-lg hover:-translate-y-1 ${
                  isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
                }`}
                style={{ transitionDelay: `${800 + i * 100}ms` }}
              >
                <p className="text-sage-900 font-medium mb-2">{pkg.sessions}</p>
                <p className="text-2xl font-medium text-sage">{pkg.price}</p>
                <p className="text-sm text-sage-900/70">{pkg.perSession}</p>
              </div>
            ))}
          </div>
        </div>
        
        <p className={`text-center mt-8 text-sage-900/70 text-sm transition-all duration-700 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`} style={{ transitionDelay: '1100ms' }}>
          {t.pricingCancel}
        </p>
      </div>
    </section>
  );
};

// Booking Section
const BookingSection = () => {
  const { t, language } = useLanguage();

  const infoItems = [
    { icon: Calendar, title: t.bookingAvailability, lines: [t.bookingWeekdays, t.bookingWeekend] },
    { icon: MapPin, title: t.bookingFormatTitle, lines: [t.bookingFormatDesc], mapsLink: true },
    { icon: Clock, title: t.bookingDurationTitle, lines: [t.bookingDurationValue] },
  ];

  return (
    <section
      id="programare"
      data-testid="booking-section"
      className="py-8 md:py-12 bg-ivory"
    >
      <div className="container-page">
        <div className="relative rounded-[28px] overflow-hidden">
          <img
            {...imgProps('contact', '100vw')}
            alt=""
            data-testid="booking-bg-image"
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover object-right"
          />
          <div className="absolute inset-0 bg-[#DDE4D8]/85"></div>

          <div className="relative px-6 md:px-14 py-12 md:py-16">
            <div className="grid lg:grid-cols-[1fr_460px] gap-10 lg:gap-16 items-start">
              <div>
                <p className="text-[11px] md:text-[12px] uppercase tracking-[0.28em] text-sage-900/70 mb-3">
                  {t.bookingEyebrow}
                </p>
                <h2 className="font-serif text-[2.4rem] md:text-[3.4rem] leading-[1.05] text-sage-900 mb-4">
                  {t.bookingTitle}
                </h2>
                <p className="text-[16px] md:text-[17px] text-sage-900/70 leading-relaxed max-w-xl mb-9">
                  {t.bookingDesc}
                </p>

                <div className="space-y-7">
                  {infoItems.map((item, i) => (
                    <div key={i} data-testid={`booking-info-${i}`} className="flex items-start gap-5">
                      <div className="w-12 h-12 shrink-0 rounded-full bg-white/55 border border-white/70 flex items-center justify-center">
                        <item.icon size={20} strokeWidth={1.5} className="text-sage-900/70" />
                      </div>
                      <div>
                        <h3 className="text-[17px] md:text-[18px] text-sage-900 mb-1">{item.title}</h3>
                        {item.lines.map((line, j) => (
                          <p key={j} className="text-[15px] text-sage-900/70 leading-relaxed max-w-md">{line}</p>
                        ))}
                        {item.mapsLink && (
                          <a
                            data-testid="booking-maps-link"
                            href={GOOGLE_MAPS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block mt-1.5 text-[14px] text-sage-900/70 underline decoration-sage-900/25 underline-offset-4 hover:text-sage-900 hover:decoration-sage-900/50 transition-colors"
                          >
                            {t.bookingMapsLink}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#FAF7F0] rounded-[22px] p-8 md:p-10 shadow-[0_18px_50px_-24px_rgba(49,66,57,0.35)]">
                <div className="flex items-center justify-center gap-4 mb-5">
                  <Video size={30} strokeWidth={1.4} className="text-sage-900/70" />
                  <span className="w-px h-7 bg-sage-900/15"></span>
                  <MapPin size={30} strokeWidth={1.4} className="text-sage-900/70" />
                </div>
                <h3 className="font-serif text-[1.8rem] text-center text-sage-900 mb-3">{t.bookingCardTitle}</h3>
                <p className="text-center text-[15px] text-sage-900/70 leading-relaxed mb-8">
                  {t.bookingCardDesc}
                </p>
                <div className="flex flex-col gap-3">
                  <a
                    data-testid="booking-calendly-btn"
                    href="https://calendly.com/ruxandrasterbet/therapy-session?month=2026-09"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2.5 bg-[#AA6244] hover:bg-[#8F5236] text-white px-8 py-4 rounded-full font-medium transition-all duration-300 shadow-md hover:shadow-lg"
                  >
                    <Calendar size={18} strokeWidth={1.6} />
                    {t.bookingCalendlyCta}
                    <ArrowRight size={17} strokeWidth={1.8} />
                  </a>
                </div>
                <p data-testid="booking-privacy-notice" className="mt-5 text-[13px] leading-relaxed text-sage-900/70">
                  {t.bookingPrivacyNotice}{' '}
                  <a
                    data-testid="booking-privacy-policy-link"
                    href={language === 'en' ? '/en/privacy-policy' : '/politica-de-confidentialitate'}
                    className="underline decoration-sage-900/30 underline-offset-4 hover:text-sage-900"
                  >
                    {language === 'en' ? 'Privacy Policy' : 'Politica de confidențialitate'}
                  </a>
                  .
                </p>
              </div>
            </div>

            <div
              data-testid="booking-question-block"
              className="mt-10 bg-[#F4F1E9]/90 rounded-[22px] px-7 md:px-10 py-8 flex flex-col lg:flex-row lg:items-center gap-7"
            >
              <div className="w-12 h-12 shrink-0 rounded-full bg-white/70 border border-white flex items-center justify-center">
                <MessageCircle size={20} strokeWidth={1.5} className="text-sage-900/70" />
              </div>
              <div className="flex-grow">
                <h3 className="font-serif text-[1.55rem] text-sage-900 mb-1.5">{t.questionHeading}</h3>
                <p className="text-[15px] text-sage-900/70 leading-relaxed max-w-2xl">{t.questionText}</p>
                <div data-testid="booking-social" className="mt-4 flex items-center gap-3">
                  <span className="text-[13px] text-sage-900/70">{t.socialConnect}</span>
                  <a
                    data-testid="booking-linkedin-link"
                    href={SOCIAL_LINKS.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={socialAria('LinkedIn', language)}
                    className="p-1 -m-1 text-sage-900/70 hover:text-sage-900 transition-colors rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-sage/50"
                  >
                    <Linkedin size={18} strokeWidth={1.5} />
                  </a>
                  <a
                    data-testid="booking-instagram-link"
                    href={SOCIAL_LINKS.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={socialAria('Instagram', language)}
                    className="p-1 -m-1 text-sage-900/70 hover:text-sage-900 transition-colors rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-sage/50"
                  >
                    <Instagram size={18} strokeWidth={1.5} />
                  </a>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                <a
                  data-testid="booking-whatsapp-link"
                  href="https://wa.me/qr/222ZB3WCDT47H1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-sage hover:bg-sage-dark text-white px-6 py-3.5 rounded-full text-[15px] font-medium transition-all duration-300"
                >
                  <MessageCircle size={17} strokeWidth={1.75} />
                  {t.questionWhatsapp}
                </a>
                <a
                  data-testid="booking-email-link"
                  href="mailto:ruxandrasterbet@gmail.com"
                  className="inline-flex items-center justify-center gap-2 bg-white border border-[#B8C0B8] hover:border-sage text-sage-900 px-6 py-3.5 rounded-full text-[15px] font-medium transition-all duration-300 break-all"
                >
                  <Mail size={17} strokeWidth={1.75} />
                  ruxandrasterbet@gmail.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const { t } = useLanguage();
  const [sectionRef, isVisible] = useReveal(0.2);
  
  const faqs = [
    { question: t.faqQ1, answer: t.faqA1 },
    { question: t.faqQ2, answer: t.faqA2 },
    { question: t.faqQ3, answer: t.faqA3 },
    { question: t.faqQ5, answer: t.faqA5 },
    { question: t.faqQ6, answer: t.faqA6 },
    { question: t.faqQ7, answer: t.faqA7 },
  ];
  
  return (
    <section 
      id="faq"
      ref={sectionRef}
      data-testid="faq-section"
      className="py-7 md:py-10 bg-cream overflow-hidden"
    >
      <div className="container-narrow">
        <div className={`text-center mb-7 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <p className="text-sm uppercase tracking-widest text-sage mb-3 font-medium">{t.faqLabel}</p>
          <h2 className="font-serif text-3xl md:text-4xl text-sage-900 mb-0">
            {t.faqTitle}
          </h2>
        </div>
        
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((faq, i) => (
            <AccordionItem 
              key={i} 
              value={`item-${i}`}
              data-testid={`faq-item-${i}`}
              className={`bg-white rounded-2xl border border-stone-100 px-7 overflow-hidden transition-all duration-500 hover:shadow-lg hover:border-sage/30 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
              }`}
              style={{ transitionDelay: `${200 + i * 100}ms` }}
            >
              <AccordionTrigger className="text-left font-medium text-[17px] md:text-[18px] text-sage-900 hover:text-sage py-5 hover:no-underline transition-colors duration-300">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-[16px] md:text-[16.5px] text-sage-900/70 pb-6 whitespace-pre-line leading-[1.75]">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const { t } = useLanguage();
  const [sectionRef, isVisible] = useReveal(0.3);
  
  return (
    <section 
      ref={sectionRef}
      data-testid="testimonials-section"
      className="py-10 md:py-16 bg-cream-dark overflow-hidden"
    >
      <div className="container-content">
        <div className={`text-center mb-10 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <p className="text-sm uppercase tracking-widest text-sage mb-4 font-medium">{t.testimonialLabel}</p>
          <h2 className="font-serif text-3xl md:text-4xl text-sage-900 mb-4">
            {t.testimonialTitle}
          </h2>
        </div>
        
        <div className={`bg-white p-8 md:p-12 rounded-2xl shadow-lg max-w-3xl mx-auto text-center transition-all duration-1000 hover:shadow-xl ${
          isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`} style={{ transitionDelay: '200ms' }}>
          <Quote className="text-sage/30 mx-auto mb-6 animate-pulse-subtle" size={48} strokeWidth={1} />
          <p className="font-serif text-xl md:text-2xl text-sage-900 mb-6 italic leading-relaxed">
            {t.testimonialQuote}
          </p>
          <p className="text-sage-900/70">
            {t.testimonialText} <span className="text-sage font-medium">{t.testimonialHighlight}</span>
          </p>
        </div>
      </div>
    </section>
  );
};

// Contact Form Section
const ContactFormSection = () => {
  const { t } = useLanguage();
  const [sectionRef, isVisible] = useReveal(0.2);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    sessionType: 'online',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.status === 200 || response.status === 201) {
        toast.success(t.contactSuccess, { duration: 5000 });
        setFormData({
          name: '',
          email: '',
          phone: '',
          sessionType: 'online',
          subject: '',
          message: '',
        });
      }
    } catch (error) {
      toast.error(t.contactError, { duration: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <section 
      id="contact"
      ref={sectionRef}
      data-testid="contact-section"
      className="py-10 md:py-16 bg-cream overflow-hidden"
    >
      <div className="container-reading">
        <div className={`text-center mb-8 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <p className="text-sm uppercase tracking-widest text-sage mb-4 font-medium">{t.contactLabel}</p>
          <h2 className="font-serif text-3xl md:text-4xl text-sage-900 mb-4">
            {t.contactTitle}
          </h2>
          <p className="text-sage-900/70">
            {t.contactSubtitle}
          </p>
        </div>
        
        <form 
          onSubmit={handleSubmit} 
          className={`bg-white p-8 rounded-2xl shadow-lg border border-stone-100 transition-all duration-1000 hover:shadow-xl ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
          style={{ transitionDelay: '200ms' }}
        >
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className={`transition-all duration-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`} style={{ transitionDelay: '300ms' }}>
              <label className="block text-sm font-medium text-sage-900 mb-2">
                {t.contactName} *
              </label>
              <input
                type="text"
                required
                data-testid="contact-name-input"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder={t.contactNamePlaceholder}
                className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-sage focus:ring-1 focus:ring-sage outline-none transition-all duration-300 hover:border-sage/50"
              />
            </div>
            <div className={`transition-all duration-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}`} style={{ transitionDelay: '400ms' }}>
              <label className="block text-sm font-medium text-sage-900 mb-2">
                {t.contactEmail} *
              </label>
              <input
                type="email"
                required
                data-testid="contact-email-input"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder={t.contactEmailPlaceholder}
                className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-sage focus:ring-1 focus:ring-sage outline-none transition-all duration-300 hover:border-sage/50"
              />
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className={`transition-all duration-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`} style={{ transitionDelay: '500ms' }}>
              <label className="block text-sm font-medium text-sage-900 mb-2">
                {t.contactPhone}
              </label>
              <input
                type="tel"
                data-testid="contact-phone-input"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder={t.contactPhonePlaceholder}
                className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-sage focus:ring-1 focus:ring-sage outline-none transition-all duration-300 hover:border-sage/50"
              />
            </div>
            <div className={`transition-all duration-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}`} style={{ transitionDelay: '600ms' }}>
              <label className="block text-sm font-medium text-sage-900 mb-2">
                {t.contactSessionType}
              </label>
              <select
                data-testid="contact-session-type"
                value={formData.sessionType}
                onChange={(e) => setFormData({...formData, sessionType: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-sage focus:ring-1 focus:ring-sage outline-none transition-all duration-300 bg-white hover:border-sage/50"
              >
                <option value="online">{t.contactOnline}</option>
                <option value="cabinet">{t.contactCabinet}</option>
              </select>
            </div>
          </div>
          
          <div className={`mb-6 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '700ms' }}>
            <label className="block text-sm font-medium text-sage-900 mb-2">
              {t.contactSubject}
            </label>
            <input
              type="text"
              data-testid="contact-subject-input"
              value={formData.subject}
              onChange={(e) => setFormData({...formData, subject: e.target.value})}
              placeholder={t.contactSubjectPlaceholder}
              className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-sage focus:ring-1 focus:ring-sage outline-none transition-all duration-300 hover:border-sage/50"
            />
          </div>
          
          <div className={`mb-6 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '800ms' }}>
            <label className="block text-sm font-medium text-sage-900 mb-2">
              {t.contactMessage} *
            </label>
            <textarea
              required
              rows={4}
              data-testid="contact-message-input"
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              placeholder={t.contactMessagePlaceholder}
              className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-sage focus:ring-1 focus:ring-sage outline-none transition-all duration-300 resize-none hover:border-sage/50"
            />
          </div>
          
          <div className={`flex items-start gap-2 mb-6 text-sm text-sage-900/70 transition-all duration-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '900ms' }}>
            <Shield size={16} className="mt-0.5 flex-shrink-0" />
            <span>{t.contactPrivacy}</span>
          </div>
          
          <button
            type="submit"
            disabled={isSubmitting}
            data-testid="contact-submit-btn"
            className={`w-full bg-warm-orange hover:bg-warm-orangeDark disabled:bg-warm-orange/50 text-white py-4 rounded-full font-medium transition-all duration-500 shadow-lg hover:shadow-xl hover:scale-[1.02] ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: '1000ms' }}
          >
            {isSubmitting ? t.contactSubmitting : t.contactSubmit}
          </button>
        </form>
      </div>
    </section>
  );
};

// Footer
const NOT_FOUND_COPY = {
  ro: {
    title: 'Pagina nu a fost găsită',
    text: 'Linkul accesat nu mai există sau a fost mutat. Poți reveni la pagina principală.',
    cta: 'Înapoi la pagina principală',
  },
  en: {
    title: 'Page not found',
    text: 'The link you followed no longer exists or has moved. You can go back to the homepage.',
    cta: 'Back to the homepage',
  },
};

const NotFoundPage = ({ language }) => {
  const copy = NOT_FOUND_COPY[language] || NOT_FOUND_COPY.ro;

  useEffect(() => {
    document.title = `404 — ${copy.title} | Ruxandra Sterbet`;
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', 'noindex, follow');
    return () => robots.setAttribute('content', 'index, follow');
  }, [copy.title]);

  return (
    <section data-testid="not-found-page" className="min-h-[70vh] flex items-center bg-cream pt-28 pb-20">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <p className="text-[12px] uppercase tracking-[0.28em] text-sage-900/70 mb-5">404</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-sage-900 mb-5">{copy.title}</h1>
        <p className="text-base text-sage-900/70 leading-relaxed mb-9">{copy.text}</p>
        <a
          data-testid="not-found-home-link"
          href={routeFor('home', language)}
          className="inline-flex items-center justify-center gap-2.5 bg-clay hover:bg-clay-dark text-white px-8 py-4 rounded-full text-[15.5px] font-medium transition-all duration-300"
        >
          {copy.cta}
          <ArrowRight size={17} strokeWidth={1.75} />
        </a>
      </div>
    </section>
  );
};

// Cookie banner / preference centre: loaded lazily so it never blocks the first paint.
const ConsentLayer = ({ language }) => (
  <Suspense fallback={null}>
    <CookieConsent language={language} />
  </Suspense>
);

const Footer = () => {
  const { t, language } = useLanguage();
  
  return (
    <footer 
      data-testid="footer"
      className="bg-sage-900 text-white py-7"
    >
      <div className="container-content">
        <AskAI t={t} />
        <div className="grid md:grid-cols-5 gap-8 md:gap-10 mb-7">
          <div className="md:col-span-2">
            <p className="font-serif text-2xl mb-3">Ruxandra Sterbet</p>
            <p className="text-white/70 text-[14px] mb-3 max-w-md">
              {t.footerDesc}
            </p>
            <div className="flex gap-4">
              <a data-testid="footer-whatsapp-link" href="https://wa.me/qr/222ZB3WCDT47H1" target="_blank" rel="noopener noreferrer" aria-label={language === 'ro' ? 'Scrie-i Ruxandrei Șterbeț pe WhatsApp' : 'Contact Ruxandra Sterbet on WhatsApp'} className="text-white/80 hover:text-white transition-colors">
                <Phone size={20} strokeWidth={1.5} />
              </a>
              <a data-testid="footer-email-link" href="mailto:ruxandrasterbet@gmail.com" aria-label={language === 'ro' ? 'Trimite un email Ruxandrei Șterbeț' : 'Email Ruxandra Sterbet'} className="text-white/80 hover:text-white transition-colors">
                <Mail size={20} strokeWidth={1.5} />
              </a>
              <a
                data-testid="footer-linkedin-link"
                href={SOCIAL_LINKS.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={socialAria('LinkedIn', language)}
                className="text-white/70 hover:text-white transition-colors rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
              >
                <Linkedin size={20} strokeWidth={1.5} />
              </a>
              <a
                data-testid="footer-instagram-link"
                href={SOCIAL_LINKS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={socialAria('Instagram', language)}
                className="text-white/70 hover:text-white transition-colors rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
              >
                <Instagram size={20} strokeWidth={1.5} />
              </a>
            </div>

            <div data-testid="footer-location" className="mt-5">
              <p className="font-medium mb-1.5">{t.footerLocationTitle}</p>
              <address className="not-italic text-white/70 text-[14px] leading-relaxed">
                <a
                  data-testid="footer-maps-link"
                  href={GOOGLE_MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${t.footerAddressLine1}, ${t.footerAddressLine2} — ${t.bookingMapsAria}`}
                  className="hover:text-white transition-colors"
                >
                  {t.footerAddressLine1}
                  <br />
                  {t.footerAddressLine2}
                </a>
              </address>
              <p className="text-white/70 text-[13px] leading-relaxed mt-1.5 max-w-xs">{t.footerLocationNote}</p>
            </div>
          </div>
          
          <div className="md:col-span-2">
            <p className="font-medium mb-3">{t.footerNav}</p>
            <ul className="space-y-1.5">
              {[
                { label: t.navAbout, href: routeFor('about-full', language) },
                { label: t.svc1Title, href: routeFor('service-psihoterapie-adulti', language) },
                { label: t.svc2Title, href: routeFor('service-consiliere-copii', language) },
                { label: t.svc3Title, href: routeFor('service-consiliere-vocationala', language) },
                { label: t.navBlog, href: routeFor('blog', language) },
                { label: t.navBlogArticles, href: routeFor('blog-articles', language) },
                { label: t.navBlogResources, href: routeFor('resources', language) },
                { label: t.navCta, href: CALENDLY_URL, external: true },
              ].map((item, i) => (
                <li key={i}>
                  <a href={item.href} {...(item.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})} className="block whitespace-nowrap text-[13.5px] text-white/70 hover:text-white transition-colors">
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <p className="font-medium mb-3">{t.footerSchedule}</p>
            <p className="text-white/70 text-[14px] mb-1.5">{t.bookingWeekdays}</p>
            <p className="text-white/70 text-[14px]">{t.bookingWeekend}</p>
          </div>
        </div>
        
        <div className="border-t border-white/10 pt-5 flex flex-col md:flex-row justify-between items-center gap-3">
          <p className="text-white/70 text-sm">
            © {new Date().getFullYear()} Ruxandra Sterbet. {t.footerRights}
          </p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm">
            <a data-testid="footer-privacy-link" href={routeFor('privacy-policy', language)} className="text-white/70 hover:text-white transition-colors">{t.footerPrivacy}</a>
            <a data-testid="footer-cookie-policy-link" href={routeFor('cookie-policy', language)} className="text-white/70 hover:text-white transition-colors">{t.footerCookies}</a>
            <a data-testid="footer-terms-link" href={routeFor('terms-of-use', language)} className="text-white/70 hover:text-white transition-colors">{t.footerTerms}</a>
            <button
              type="button"
              data-testid="footer-cookie-settings-btn"
              onClick={() => window.openCookiePreferences?.()}
              className="text-white/70 hover:text-white transition-colors underline-offset-4 hover:underline"
            >
              {t.footerCookieSettings}
            </button>
          </div>
        </div>
      </div>
      <ConsentLayer language={language} />
    </footer>
  );
};

// Main App Component
function FloatingWhatsAppMount() {
  const { language } = useLanguage();
  return (
    <Suspense fallback={null}>
      <FloatingWhatsApp language={language} pathname={window.location.pathname} />
    </Suspense>
  );
}

function App() {
  const [bannerVisible] = useState(false);
  const legacyTarget = LEGACY_REDIRECTS[normalizePath(window.location.pathname)];
  const [currentPage, setCurrentPage] = useState(() => pageFromPath(window.location.pathname));
  const [selectedArticle, setSelectedArticle] = useState(() => findArticleByPath(window.location.pathname));
  
  // Check URL for routing
  useEffect(() => {
    if (legacyTarget) {
      window.location.replace(legacyTarget);
      return;
    }
    setCurrentPage(pageFromPath(window.location.pathname));
    
    // Handle browser back/forward
    const handlePopState = () => {
      setCurrentPage(pageFromPath(window.location.pathname));
      setSelectedArticle(findArticleByPath(window.location.pathname));
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const urlLang = langFromPath(window.location.pathname);
    if (urlLang) localStorage.setItem('lang', urlLang);
    applyRouteMeta(urlLang || localStorage.getItem('lang') || 'ro');
  }, [currentPage, selectedArticle]);
  
  const navigateTo = (page, article = null, scrollToId = null) => {
    if (PAGE_ROUTES[page]) {
      const lang = localStorage.getItem('lang') || 'ro';
      window.history.pushState({}, '', routeFor(page, lang));
    } else if (page === 'article' && article) {
      window.history.pushState({}, '', articleRoute(article));
    } else {
      window.history.pushState({}, '', '/');
    }
    setCurrentPage(page);
    setSelectedArticle(article);
    window.scrollTo(0, 0);
    if (scrollToId) {
      setTimeout(() => {
        document.getElementById(scrollToId)?.scrollIntoView({ behavior: 'smooth' });
      }, 150);
    }
  };
  
  return (
    <LanguageProvider>
      {legacyTarget ? null : (
        <>
          <AppContent 
            bannerVisible={bannerVisible}
            currentPage={currentPage}
            selectedArticle={selectedArticle}
            navigateTo={navigateTo}
          />
          <FloatingWhatsAppMount />
        </>
      )}
    </LanguageProvider>
  );
}

// App Content with Language Context
function AppContent({ bannerVisible, currentPage, selectedArticle, navigateTo }) {
  const { language, t } = useLanguage();
  
  // Keep the URL in sync with the active language on localized pages
  useEffect(() => {
    const route = PAGE_ROUTES[currentPage];
    const path = normalizePath(window.location.pathname);
    // only swap the URL between the two languages of the page we are actually on
    if (route && path !== route[language] && (path === route.ro || path === route.en)) {
      window.history.replaceState({}, '', route[language]);
    }
    applyRouteMeta(language);
  }, [language, currentPage]);

  // On an article, switching the language goes straight to the translated article.
  // If no translation exists yet, fall back safely to the Blog landing page in the
  // requested language so the user is never stranded on a mismatched-language article.
  useEffect(() => {
    if (currentPage !== 'article' || !selectedArticle) return;
    if (selectedArticle.language === language) return;
    const other = translationFor(selectedArticle);
    if (other) navigateTo('article', other);
    else navigateTo('blog');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, currentPage, selectedArticle]);

  // Genuine 404 view for unknown URLs
  if (currentPage === 'not-found') {
    return (
      <div className="App bg-cream min-h-screen">
        <Navigation bannerVisible={false} navigateTo={navigateTo} />
        <NotFoundPage language={language} />
        <Footer />
      </div>
    );
  }

  // Permanently discontinued services (served with HTTP 410 on the host)
  if (currentPage === 'service-retired') {
    return (
      <div className="App bg-cream min-h-screen">
        <Navigation bannerVisible={false} navigateTo={navigateTo} />
        <Suspense fallback={<PageFallback />}>
          <ServiceRetiredPage language={language} kind={retiredRoute(normalizePath(window.location.pathname))?.kind} />
        </Suspense>
        <Footer />
      </div>
    );
  }

  // Legal / compliance pages
  if (currentPage === 'privacy-policy' || currentPage === 'cookie-policy' || currentPage === 'terms-of-use') {
    return (
      <div className="App bg-cream min-h-screen">
        <Navigation bannerVisible={false} navigateTo={navigateTo} />
        <Suspense fallback={<PageFallback />}>
          <LegalPage docKey={currentPage} language={language} />
        </Suspense>
        <Footer />
      </div>
    );
  }
  
  // Render full About page
  if (currentPage === 'about-full') {
    return (
      <div className="App bg-cream min-h-screen">
        <Toaster position="top-center" richColors />
        <Navigation bannerVisible={false} navigateTo={navigateTo} />
        <Suspense fallback={<PageFallback />}>
          <AboutFullPage 
            t={t}
            language={language}
            onBack={() => navigateTo('home', null, 'despre')}
            onBook={() => navigateTo('home', null, 'programare')}
            onServices={() => navigateTo('home', null, 'servicii')}
          />
        </Suspense>
        <Footer />
      </div>
    );
  }
  
  // Render Service detail pages
  if (currentPage.startsWith('service-')) {
    return (
      <div className="App bg-cream min-h-screen">
        <Toaster position="top-center" richColors />
        <Navigation bannerVisible={false} navigateTo={navigateTo} />
        <Suspense fallback={<PageFallback />}>
          <ServiceDetailPage 
            serviceId={currentPage}
            t={t}
            language={language}
            onBack={() => navigateTo('home', null, 'servicii')}
            onBook={() => navigateTo('home', null, 'programare')}
            onNavigate={(id) => navigateTo(id)}
          />
        </Suspense>
        <Footer />
      </div>
    );
  }

  // Render Blog landing page
  if (currentPage === 'blog') {
    return (
      <div className="App bg-cream min-h-screen">
        <Toaster position="top-center" richColors />
        <Navigation bannerVisible={false} navigateTo={navigateTo} />
        <Suspense fallback={<PageFallback />}>
          <BlogLandingPage
            t={t}
            language={language}
            articles={articlesFor(language)}
            onNavigate={(page) => navigateTo(page)}
            onSelectArticle={(article) => navigateTo('article', article)}
          />
        </Suspense>
        <Footer />
      </div>
    );
  }

  // Render Resources page
  if (currentPage === 'resources') {
    return (
      <div className="App bg-cream min-h-screen">
        <Toaster position="top-center" richColors />
        <Navigation bannerVisible={false} navigateTo={navigateTo} />
        <Suspense fallback={<PageFallback />}>
          <ResourcesPage language={language} />
        </Suspense>
        <Footer />
      </div>
    );
  }

  if (currentPage === 'blog-articles') {
    return (
      <div className="App bg-cream min-h-screen">
        <Toaster position="top-center" richColors />
        <Navigation bannerVisible={false} navigateTo={navigateTo} />
        <ArticlesPage 
          t={t}
          language={language}
          onSelectArticle={(article) => navigateTo('article', article)}
          onNavigate={(page) => navigateTo(page)}
        />
        <Footer />
      </div>
    );
  }
  
  // Render Single Article Page (static content, resolved from the URL)
  if (currentPage === 'article' && selectedArticle) {
    return (
      <div className="App bg-cream min-h-screen">
        <Toaster position="top-center" richColors />
        <Navigation bannerVisible={false} navigateTo={navigateTo} />
        <ArticlePage 
          article={selectedArticle}
          language={language}
          onSelectArticle={(article) => navigateTo('article', article)}
        />
        <Footer />
      </div>
    );
  }
  
  // Render Home Page
  return (
    <div className="App bg-cream min-h-screen">
      <Toaster position="top-center" richColors />
      <Navigation bannerVisible={bannerVisible} navigateTo={navigateTo} />
      
      <main>
        <HeroSection />
        <ValidationSection />
        <PerspectiveSection />
        <ServicesSection navigateTo={navigateTo} />
        <HowItWorksSection />
        <AboutSection navigateTo={navigateTo} />
        <BookingSection />
        {articlesFor(language).length > 0 && <ArticlesSectionWithNav navigateTo={navigateTo} />}
        <FAQSection />
      </main>
      
      <Footer />
    </div>
  );
}

// Articles section on the home page (rendered only when genuine articles exist)
function ArticlesSectionWithNav({ navigateTo }) {
  const { t, language } = useLanguage();
  const [sectionRef, isVisible] = useReveal(0.2);
  const articles = articlesFor(language).slice(0, 3);
  
  const categories = [
    { title: t.articleCat1, icon: Brain, id: 'teme-terapeutice' },
    { title: t.articleCat3, icon: MessageCircle, id: 'despre-terapie' },
    { title: t.articleCat4, icon: Heart, id: 'sanatate-mentala' },
    { title: t.articleCat5, icon: BookOpen, id: 'recomandari' },
  ];
  
  return (
    <section 
      id="articole"
      ref={sectionRef}
      data-testid="articles-section"
      className="py-10 md:py-16 bg-cream-dark overflow-hidden"
    >
      <div className="container-content">
        <div className={`text-center mb-10 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <p className="text-sm uppercase tracking-widest text-sage mb-4 font-medium">{t.articlesLabel}</p>
          <h2 className="font-serif text-3xl md:text-4xl text-sage-900 mb-4">
            {t.articlesTitle}
          </h2>
          <p className="text-sage-900/70">{t.articlesSubtitle}</p>
        </div>
        
        {/* Categories */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {categories.map((cat, i) => (
            <div 
              key={i}
              data-testid={`article-category-${i}`}
              onClick={() => navigateTo('blog-articles')}
              className={`bg-white p-6 rounded-2xl border border-stone-100 hover:border-sage/30 hover:shadow-xl transition-all duration-500 text-center cursor-pointer group hover:-translate-y-2 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${200 + i * 100}ms` }}
            >
              <cat.icon className="text-sage mx-auto mb-3 transition-all duration-500 group-hover:scale-125 group-hover:text-warm-orange" size={28} strokeWidth={1.5} />
              <h3 className="font-medium text-sage-900 text-sm group-hover:text-sage transition-colors duration-300">{cat.title}</h3>
            </div>
          ))}
        </div>
        
        {/* Recent Articles */}
        {articles.length > 0 && (
          <div className={`transition-all duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '600ms' }}>
            <h3 className="font-serif text-xl text-sage-900 mb-6 text-center">
              {language === 'ro' ? 'Articole recente' : 'Recent articles'}
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              {articles.map(article => (
                <ArticleCard 
                  key={article.id}
                  article={article}
                  language={language}
                  onClick={() => navigateTo('article', article)}
                />
              ))}
            </div>
          </div>
        )}
        
        {/* View all button */}
        <div className={`text-center mt-8 transition-all duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '800ms' }}>
          <button
            onClick={() => navigateTo('blog-articles')}
            className="inline-flex items-center gap-2 text-sage hover:text-sage-dark font-medium transition-colors"
          >
            {language === 'ro' ? 'Vezi toate articolele' : 'View all articles'}
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
}

export default App;
